/**
 * "Em qual atividade?" (PO, 22/09/2026) — a folha que escolhe onde a sugestão
 * entra no roteiro. Quem administra vincula na hora; os demais mandam um
 * pedido que aparece para o dono e o admin.
 */
import { useQuery } from '@tanstack/react-query';

import { Sheet } from '../../ui/Sheet';
import { Card } from '../../ui/primitives';
import { useTrip } from '../../trip/TripContext';

export interface AtividadeEscolhivel {
  id: string;
  title: string;
  date: string;
  startTime: string;
}

const diaCurto = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

export function EscolherAtividadeSheet({
  titulo,
  ocupado,
  onEscolher,
  onFechar,
}: {
  titulo: string;
  ocupado: boolean;
  onEscolher: (activityId: string) => void;
  onFechar: () => void;
}) {
  const { tripId } = useTrip();
  const atividades = useQuery({
    queryKey: ['roteiro', tripId, 'atividades-chapadas'],
    queryFn: async () => {
      const res = await fetch(`/api/v1/trips/${tripId}/activities`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Não foi possível carregar o roteiro.');
      return (await res.json()) as { activities: AtividadeEscolhivel[] };
    },
    staleTime: 15_000,
  });
  const lista = atividades.data?.activities ?? [];

  return (
    <Sheet titleId="escolher-atividade-titulo" onFechar={onFechar} testId="escolher-atividade">
      <h2 className="sheet__title" id="escolher-atividade-titulo">{titulo}</h2>
      <Card variant="surf">
        {atividades.isLoading && <p className="auth-note">Carregando o roteiro…</p>}
        {atividades.isSuccess && lista.length === 0 && (
          <p className="auth-note" data-testid="escolher-atividade-vazio">
            O roteiro ainda não tem atividades. Crie uma no Roteiro e volte aqui.
          </p>
        )}
        {lista.map((a) => (
          <button
            type="button"
            key={a.id}
            className="sug__atividade-opcao"
            disabled={ocupado}
            onClick={() => onEscolher(a.id)}
            data-testid="escolher-atividade-opcao"
          >
            <span className="sug__atividade-nome">{a.title}</span>
            <span className="sug__atividade-quando mono">{diaCurto(a.date)} · {a.startTime}</span>
          </button>
        ))}
      </Card>
      <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={onFechar}>
        Cancelar
      </button>
    </Sheet>
  );
}
